/**
 * AgentGrid - Mobile-optimized grid/list view of all agents
 * 
 * Displays agents in a responsive card grid or list
 * with sorting, search, and view toggle options.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  Plus, 
  Search, 
  LayoutGrid, 
  LayoutList,
  ArrowUpDown,
  Clock,
  Users,
  SortAsc,
  ChevronDown,
  ChevronRight,
  Loader2,
  MoreVertical,
  Share2,
  Trash2,
  Edit3,
  Check,
  X
} from 'lucide-react';

// Sort options
const SORT_OPTIONS = [
  { id: 'updated', label: 'Last Updated', icon: Clock },
  { id: 'leads', label: 'Most Leads', icon: Users },
  { id: 'name', label: 'Name A-Z', icon: SortAsc },
  { id: 'created', label: 'Recently Created', icon: Clock },
];

// Sort function
const sortAgents = (agents, sortBy) => {
  const sorted = [...agents];
  
  switch (sortBy) {
    case 'updated':
      return sorted.sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      });
    case 'leads':
      return sorted.sort((a, b) => {
        const countA = a.leads_count || a.leads?.length || 0;
        const countB = b.leads_count || b.leads?.length || 0;
        return countB - countA;
      });
    case 'name':
      return sorted.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    case 'created':
      return sorted.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
    default:
      return sorted;
  }
};

// Helper function for relative time
function formatRelativeTime(dateString) {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Count leads by status
function countLeadsByStatus(leads = []) {
  let liked = 0;
  let archived = 0;
  
  leads.forEach(lead => {
    const status = lead.status || 'inbox';
    if (status === 'liked' || status === 'shortlist') liked++;
    if (status === 'archived') archived++;
  });
  
  return { liked, archived };
}

// Get preview of liked leads (for showing in agent cards)
function getLikedLeadsPreview(leads = []) {
  return leads.filter(lead => {
    const status = lead.status || 'inbox';
    return status === 'liked' || status === 'shortlist';
  });
}

export default function AgentGrid({ 
  agents = [], 
  isLoading = false,
  loadingAgentId = null, // NEW: Shows loading state on specific agent
  onSelectAgent,
  onNewAgent,
  onDeleteAgent,
  onShareAgent,
  onRenameAgent,
  selectedAgentId = null,
  viewMode = 'list', // Default to list for mobile
  onViewModeChange,
  hideHeader = false, // Hide header when used in slide-out panel
  compact = false // Compact mode for embedded use
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  
  const containerRef = useRef(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpenId !== null && containerRef.current && !e.target.closest('[data-menu-trigger]') && !e.target.closest('[data-menu-dropdown]')) {
        setMenuOpenId(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpenId]);
  
  // Close menu when clicking anywhere else
  const handleAgentClick = (agent) => {
    // Don't navigate if menu is open
    if (menuOpenId !== null) {
      setMenuOpenId(null);
      return;
    }
    onSelectAgent?.(agent);
  };

  // Filter and sort agents
  const filteredAgents = useMemo(() => {
    let result = agents;
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(agent => 
        agent.name?.toLowerCase().includes(query) ||
        agent.natural_query?.toLowerCase().includes(query)
      );
    }
    
    // Sort
    return sortAgents(result, sortBy);
  }, [agents, searchQuery, sortBy]);

  const currentSort = SORT_OPTIONS.find(opt => opt.id === sortBy);

  return (
    <div ref={containerRef} className="flex flex-col h-full">
      {/* Controls Bar - Always visible */}
      <div className={`flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 border-b border-black/5 bg-white/80 backdrop-blur-sm sticky top-0 z-10 ${hideHeader ? '' : 'pt-4'}`}>
        {/* Header - only if not hidden */}
        {/* Title hidden for cleaner UI */}
        
        {/* Search - Full width on mobile */}
        <div className="relative flex-1 sm:flex-initial sm:w-48 lg:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-black/10 rounded-lg text-sm text-black placeholder-black/40 focus:outline-none focus:border-[#5a20ff]/30 transition-all"
          />
        </div>

        {/* Sort & View Controls */}
        <div className="flex items-center gap-2">
          {/* Sort Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-black/10 rounded-lg text-sm text-black/70 hover:border-black/20 transition-colors"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{currentSort?.label}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
            </button>
            
            {showSortMenu && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowSortMenu(false)} 
                />
                <div className="absolute right-0 mt-1 z-50 bg-white rounded-lg shadow-xl border border-black/10 py-1 min-w-[140px]">
                  {SORT_OPTIONS.map(option => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        onClick={() => {
                          setSortBy(option.id);
                          setShowSortMenu(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                          sortBy === option.id 
                            ? 'text-[#5a20ff] bg-[#5a20ff]/5' 
                            : 'text-black/70 hover:bg-black/5'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* View Toggle */}
          <div className="flex items-center p-1 bg-black/5 rounded-lg">
            <button
              onClick={() => onViewModeChange?.('grid')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
              }`}
              title="Grid View"
            >
              <LayoutGrid className={`w-4 h-4 ${viewMode === 'grid' ? 'text-[#5a20ff]' : 'text-black/40'}`} />
            </button>
            <button
              onClick={() => onViewModeChange?.('list')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
              }`}
              title="List View"
            >
              <LayoutList className={`w-4 h-4 ${viewMode === 'list' ? 'text-[#5a20ff]' : 'text-black/40'}`} />
            </button>
          </div>

          {/* New Agent Button */}
          <button
            onClick={onNewAgent}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#5a20ff] text-white rounded-lg text-sm font-medium hover:bg-[#5a20ff]/90 transition-all shadow-md"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-[#5a20ff]" />
              <p className="text-sm text-black/50">Loading agents...</p>
            </div>
          </div>
        ) : filteredAgents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            {searchQuery ? (
              <>
                <Search className="w-10 h-10 text-black/20 mb-3" />
                <p className="text-base font-medium text-black/60 mb-2 text-center">
                  No agents match "{searchQuery}"
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-sm text-[#5a20ff] hover:underline"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#5a20ff]/10 to-[#52dcb3]/10 flex items-center justify-center mb-3">
                  <Plus className="w-8 h-8 text-[#5a20ff]/50" />
                </div>
                <p className="text-base font-medium text-black/60 mb-1">No agents yet</p>
                <p className="text-sm text-black/40 mb-4 text-center">Create your first agent</p>
                <button
                  onClick={onNewAgent}
                  className="flex items-center gap-2 px-4 py-2 bg-[#5a20ff] text-white rounded-lg text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Create Agent
                </button>
              </>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          // Grid View - Responsive with liked/archived counts
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {filteredAgents.map(agent => {
              const counts = countLeadsByStatus(agent.leads || []);
              const totalLeads = agent.leads_count || agent.leads?.length || 0;
              
              const isLoadingThis = loadingAgentId === agent.id;
              
              return (
                <div
                  key={agent.id}
                  onClick={() => !isLoadingThis && handleAgentClick(agent)}
                  className={`relative bg-white rounded-xl border-2 p-4 transition-all ${
                    isLoadingThis 
                      ? 'opacity-70 cursor-wait' 
                      : 'cursor-pointer hover:shadow-md hover:border-[#5a20ff]/20'
                  } ${
                    agent.id === selectedAgentId ? 'border-[#5a20ff] shadow-md' : 'border-black/5'
                  }`}
                >
                  {/* Loading overlay */}
                  {isLoadingThis && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-xl z-10">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#5a20ff]" />
                        <span className="text-sm text-black/60">Loading...</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 
                      className="font-semibold text-black line-clamp-2 flex-1"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {agent.name || 'Untitled Agent'}
                    </h3>
                    <button
                      data-menu-trigger
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === agent.id ? null : agent.id);
                      }}
                      className="p-1 hover:bg-black/5 rounded flex-shrink-0 relative z-20"
                      disabled={isLoadingThis}
                    >
                      <MoreVertical className="w-4 h-4 text-black/40" />
                    </button>
                  </div>
                  
                  {/* Liked leads preview - show first 3 liked leads */}
                  {counts.liked > 0 && agent.leads && (
                    <div className="flex items-center gap-1 mb-2 overflow-hidden">
                      {getLikedLeadsPreview(agent.leads).slice(0, 3).map((lead, idx) => {
                        const data = lead.data || lead.lead_data || lead;
                        const name = data.name || data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || '';
                        const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
                        return (
                          <div 
                            key={idx}
                            className="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 rounded-full text-xs"
                            title={name}
                          >
                            <div className="w-4 h-4 rounded-full bg-emerald-500 text-white text-[8px] font-bold flex items-center justify-center">
                              {initials}
                            </div>
                            <span className="text-emerald-700 truncate max-w-[60px]">{name.split(' ')[0]}</span>
                          </div>
                        );
                      })}
                      {counts.liked > 3 && (
                        <span className="text-xs text-emerald-600 font-medium">+{counts.liked - 3}</span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {/* Liked count is PRIMARY */}
                      {counts.liked > 0 ? (
                        <span className="flex items-center gap-1 font-semibold text-emerald-600">
                          <Check className="w-3.5 h-3.5" />
                          {counts.liked} liked
                        </span>
                      ) : (
                        <span className="text-black/40">
                          {totalLeads} found
                        </span>
                      )}
                      {/* Passed count secondary */}
                      {counts.archived > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-slate-400">
                          <X className="w-3 h-3" />
                          {counts.archived}
                        </span>
                      )}
                    </div>
                    <span className="text-black/40 text-xs">
                      {formatRelativeTime(agent.updated_at || agent.created_at)}
                    </span>
                  </div>

                {/* Dropdown Menu - Positioned relative to card, not fixed overlay */}
                {menuOpenId === agent.id && (
                  <div data-menu-dropdown className="absolute top-12 right-2 z-50 bg-white rounded-lg shadow-xl border border-black/10 py-1 min-w-[120px]">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onShareAgent?.(agent.id);
                        setMenuOpenId(null);
                      }}
                      className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-black/70 hover:bg-black/5"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      Share
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newName = window.prompt('Rename agent:', agent.name);
                        if (newName) onRenameAgent?.(agent.id, newName);
                        setMenuOpenId(null);
                      }}
                      className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-black/70 hover:bg-black/5"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      Rename
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Delete this agent?')) onDeleteAgent?.(agent.id);
                        setMenuOpenId(null);
                      }}
                      className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        ) : (
          // List View - Shows LIKED count as primary badge (what matters)
          <div className="divide-y divide-black/5">
            {filteredAgents.map(agent => {
              const counts = countLeadsByStatus(agent.leads || []);
              const totalLeads = agent.leads_count || agent.leads?.length || 0;
              const hasLiked = counts.liked > 0;
              const isLoadingThis = loadingAgentId === agent.id;
              
              return (
                <div
                  key={agent.id}
                  onClick={() => !isLoadingThis && handleAgentClick(agent)}
                  className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                    isLoadingThis 
                      ? 'opacity-70 cursor-wait bg-[#5a20ff]/5' 
                      : 'cursor-pointer hover:bg-black/[0.02]'
                  } ${
                    agent.id === selectedAgentId ? 'bg-[#5a20ff]/5' : ''
                  }`}
                >
                  {/* Liked count badge - PRIMARY (green if has liked, gray if none) */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isLoadingThis
                      ? 'bg-[#5a20ff]/10'
                      : hasLiked 
                        ? 'bg-emerald-100 text-emerald-600' 
                        : totalLeads > 0
                          ? 'bg-black/5 text-black/30'
                          : 'bg-black/5 text-black/20'
                  }`}>
                    {isLoadingThis ? (
                      <Loader2 className="w-4 h-4 animate-spin text-[#5a20ff]" />
                    ) : hasLiked ? (
                      <div className="flex items-center gap-0.5">
                        <Check className="w-3 h-3" />
                        <span className="text-sm font-bold">{counts.liked}</span>
                      </div>
                    ) : (
                      <span className="text-sm font-bold">{totalLeads}</span>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p 
                      className="font-medium text-black truncate"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {agent.name || 'Untitled Agent'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-black/40">
                      {isLoadingThis ? (
                        <span className="text-[#5a20ff]">Opening agent...</span>
                      ) : (
                        <>
                          <span>{formatRelativeTime(agent.updated_at || agent.created_at)}</span>
                          {/* Show total and passed as secondary info */}
                          {totalLeads > 0 && (
                            <>
                              <span>•</span>
                              <span>{totalLeads} found</span>
                            </>
                          )}
                          {counts.archived > 0 && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-0.5 text-slate-400">
                                <X className="w-3 h-3" />
                                {counts.archived}
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Arrow or Loading */}
                  {isLoadingThis ? (
                    <div className="w-4 h-4 flex-shrink-0" /> // Spacer
                  ) : (
                    <ChevronRight className="w-4 h-4 text-black/20 flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
